// Centralized intent/conversion event tracking.
// Always fire-and-forget via sendBeacon so events survive navigation/unload.
import { supabase } from "@/integrations/supabase/client";
import { gaEvent } from "@/lib/ga";

export const track = (event: string, props: Record<string, unknown> = {}) => {
  const payload = {
    event,
    path: typeof window !== "undefined" ? window.location.pathname : null,
    ...props,
  };
  // Mirror every event into GA4 as a custom event.
  try {
    // page_view is sent separately by CTATracker via gaPageView
    if (event !== "page_view") gaEvent(event, props);
  } catch {
    // GA failures must never block internal analytics
  }

  const url = `${import.meta.env["VITE_SUPABASE_URL"]}/functions/v1/log-event`;
  const body = JSON.stringify(payload);

  // Primary: fetch with keepalive — same fire-and-forget behavior as sendBeacon,
  // but works in environments (like the Lovable preview iframe) that block beacons.
  try {
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {
        supabase.functions.invoke("log-event", { body: payload }).catch(() => {});
      });
      return;
    }
  } catch {
    // fall through
  }

  // Fallback: sendBeacon, then SDK invoke.
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    // fall through
  }
  supabase.functions.invoke("log-event", { body: payload }).catch(() => {});
};

/**
 * Fire an event at most once per browser session, keyed by event name.
 * Use for "intent" signals like first form-field focus, where re-renders
 * or repeated focus events should not produce duplicate events.
 */
export const trackOnce = (event: string, props: Record<string, unknown> = {}) => {
  try {
    const key = `tracked:${event}`;
    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    }
  } catch {
    // sessionStorage unavailable — still fire (better to over-count than miss)
  }
  track(event, props);
};
